/**
 * markerExportValidation.ts
 * Pre-flight checks for the marker DOCX flow, run before preview export / print.
 *
 * Everything here is pure so it can be unit-tested without a DOM: the caller
 * passes the current wizard state and gets back a list of issues.
 */

import type { FieldFormattingConfig } from "@/utils/formatter";
import { classifyBindStatus, lookupRecordValue } from "@/utils/fieldResolve";
import type { LayoutWarning } from "@/utils/fieldLayout";

export type ValidationSeverity = "error" | "warning";

export interface ExportIssue {
  id: string;
  severity: ValidationSeverity;
  title: string;
  message: string;
  fieldName?: string;
}

export interface ExportValidationResult {
  valid: boolean;
  hasErrors: boolean;
  hasWarnings: boolean;
  issues: ExportIssue[];
}

/** A field as resolved by the mapping step. */
export interface ValidatedField {
  fieldName: string;
  inRepeat: boolean;
  excelColumn: string;
  required: boolean;
  /** Explicitly marked as not used on this form. */
  notApplicable?: boolean;
}

/** A group the user picked for export. */
export interface ValidatedGroup {
  groupValue: string;
  rows: Record<string, unknown>[];
  headerValues: Record<string, unknown>;
}

export interface MarkerValidationInput {
  hasTemplate: boolean;
  templateHasFieldMarkers: boolean;
  templateErrors?: string[];
  fields: ValidatedField[];
  headers: string[];
  rowCount: number;
  selectedGroups: ValidatedGroup[];
  formats?: Record<string, FieldFormattingConfig>;
  /** Layout warnings collected while fitting identifier/content cells. */
  layoutWarnings?: LayoutWarning[];
  /**
   * When true, missing data (unmapped / blank cells) is allowed on the printout
   * as empty — required unmapped becomes a warning, not a hard error.
   */
  printEmptyWhenMissing?: boolean;
}

const NUMERIC_FORMATS = new Set(["number", "currency", "percent"]);

function isBlank(value: unknown): boolean {
  return value == null || String(value).trim() === "";
}

function isNumericLike(value: unknown): boolean {
  if (typeof value === "number") return !isNaN(value);
  const cleaned = String(value).replace(/[\s.,%₫]/g, "");
  return cleaned !== "" && !isNaN(Number(cleaned));
}

function isDateLike(value: unknown): boolean {
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value === "number") return value > 0;
  return !isNaN(Date.parse(String(value)));
}

function samplesForField(field: ValidatedField, groups: ValidatedGroup[]): unknown[] {
  const out: unknown[] = [];
  for (const group of groups) {
    if (field.inRepeat) {
      for (const row of group.rows) {
        out.push(lookupRecordValue(row, field.excelColumn, field.fieldName).raw);
      }
    } else {
      out.push(lookupRecordValue(group.headerValues, field.excelColumn, field.fieldName).raw);
    }
  }
  return out;
}

/**
 * Validate the wizard state before exporting or printing.
 * Errors block the action; warnings are informational.
 */
export function validateMarkerExport(input: MarkerValidationInput): ExportValidationResult {
  const issues: ExportIssue[] = [];
  let seq = 0;
  const push = (issue: Omit<ExportIssue, "id">) => {
    issues.push({ id: `issue-${++seq}`, ...issue });
  };

  if (input.rowCount === 0) {
    push({
      severity: "error",
      title: "Chưa có dữ liệu Excel",
      message: "Hãy tải lên file Excel và chọn sheet chứa dòng dữ liệu trước khi xuất.",
    });
  }

  if (!input.hasTemplate) {
    push({
      severity: "error",
      title: "Chưa chọn template",
      message: "Hãy tải lên một template DOCX trước khi xuất.",
    });
    return finish(issues);
  }

  if (!input.templateHasFieldMarkers) {
    push({
      severity: "error",
      title: "Template không có placeholder {{tên}}",
            message: "Không xác định được vị trí điền dữ liệu. Hãy kiểm tra lại template.",
    });
  }

  for (const err of input.templateErrors ?? []) {
    push({ severity: "error", title: "Lỗi cấu trúc template", message: err });
  }

  if (input.selectedGroups.length === 0) {
    push({
      severity: "error",
      title: "Chưa chọn nhóm nào để in",
      message: "Hãy thêm ít nhất một nhóm vào danh sách in.",
    });
  }

  for (const field of input.fields) {
    const samples = samplesForField(field, input.selectedGroups);
    const status = classifyBindStatus(
      field.fieldName,
      field.excelColumn,
      input.headers,
      samples,
      { notApplicable: field.notApplicable }
    );

    if (status.case === "not_applicable") {
      continue;
    }

    if (status.case === "unmapped") {
          const allowEmpty = Boolean(input.printEmptyWhenMissing);
          push({
            severity: field.required && !allowEmpty ? "error" : "warning",
            title:
              field.required && !allowEmpty
                ? "Trường bắt buộc chưa được map"
                : allowEmpty
                  ? "Thiếu dữ liệu — in rỗng"
                  : "Trường chưa được map",
            message: field.required
              ? allowEmpty
                ? `Trường "${field.fieldName}" bắt buộc chưa map — sẽ in ô trống (đang bật «Thiếu data → in rỗng»).`
                : `Trường "${field.fieldName}" là bắt buộc nhưng chưa gắn với cột Excel nào.`
              : allowEmpty
                ? `Trường "${field.fieldName}" chưa mapping — in rỗng.`
                : `Trường "${field.fieldName}" chưa mapping — sẽ để trống trên chứng từ (khác với ô map rồi nhưng không có dữ liệu).`,
            fieldName: field.fieldName,
          });
          continue;
        }

    if (status.case === "mapped_missing_column") {
      push({
        severity: "error",
        title: "Cột Excel không tồn tại",
        message: `Trường "${field.fieldName}" được map với cột "${field.excelColumn}" nhưng cột này không có trong sheet đang chọn.`,
        fieldName: field.fieldName,
      });
      continue;
    }

    if (status.case === "mapped_empty") {
      push({
        severity: "warning",
        title: "Đã map nhưng ô Excel trống",
        message: `Trường "${field.fieldName}" đã mapping tới "${field.excelColumn}" nhưng không có dữ liệu trong các nhóm đã chọn.`,
        fieldName: field.fieldName,
      });
      continue;
    }

    const formatType = input.formats?.[field.fieldName]?.formatType;
    const filled = samples.filter((v) => !isBlank(v));
    if (!formatType) continue;

    for (const group of input.selectedGroups) {
      if (NUMERIC_FORMATS.has(formatType)) {
        const groupVals = field.inRepeat
          ? group.rows.map((r) => lookupRecordValue(r, field.excelColumn, field.fieldName).raw)
          : [lookupRecordValue(group.headerValues, field.excelColumn, field.fieldName).raw];
        const bad = groupVals.filter((v) => !isBlank(v) && !isNumericLike(v));
        if (bad.length > 0) {
          push({
            severity: "warning",
            title: "Giá trị không phải số",
            message: `Trường "${field.fieldName}" đang định dạng dạng số nhưng nhóm "${group.groupValue}" có ${bad.length} giá trị không đọc được (ví dụ: "${String(bad[0])}").`,
            fieldName: field.fieldName,
          });
        }
      }

      if (formatType === "date") {
        const groupVals = field.inRepeat
          ? group.rows.map((r) => lookupRecordValue(r, field.excelColumn, field.fieldName).raw)
          : [lookupRecordValue(group.headerValues, field.excelColumn, field.fieldName).raw];
        const bad = groupVals.filter((v) => !isBlank(v) && !isDateLike(v));
        if (bad.length > 0) {
          push({
            severity: "warning",
            title: "Giá trị không phải ngày",
            message: `Trường "${field.fieldName}" đang định dạng ngày nhưng nhóm "${group.groupValue}" có ${bad.length} giá trị không parse được (ví dụ: "${String(bad[0])}").`,
            fieldName: field.fieldName,
          });
        }
      }
    }

    void filled;
  }

  for (const lw of input.layoutWarnings ?? []) {
    push({
      severity: "warning",
      title:
        lw.kind === "identifier_overflow"
          ? "Mã định danh không vừa ô"
          : lw.kind === "content_overflow"
            ? "Nội dung vượt ô"
            : "Footer bị chồng",
      message: lw.message,
      fieldName: lw.fieldHint,
    });
  }

  return finish(issues);
}

function finish(issues: ExportIssue[]): ExportValidationResult {
  const hasErrors = issues.some((i) => i.severity === "error");
  const hasWarnings = issues.some((i) => i.severity === "warning");
  return { valid: !hasErrors, hasErrors, hasWarnings, issues };
}
